/**
 * chat-helpers.ts
 * 从 chat.ts 提取的公共辅助函数，减少重复代码
 */

import type { ChatAssistantMessage } from '../types/chat'

import { isSafeStickerUrl } from './stickers'

/**
 * 情感映射表：将 AI 生成的非标准 emotion 值映射到标准值
 */
export const EMOTION_MAPPING: Record<string, string> = {
  default: 'neutral',
  idle: 'neutral',
  curious: 'question',
  confused: 'question',
  excited: 'happy',
  worried: 'awkward',
  thinking: 'think',
  playful: 'happy',
  admiring: 'happy',
  intrigued: 'think',
  interested: 'think',
  nervous: 'awkward',
  shy: 'awkward',
  amazed: 'surprise',
  shocked: 'surprise',
  surprised: 'surprise',
  upset: 'sad',
  disappointed: 'sad',
  frustrated: 'angry',
  annoyed: 'angry',
  concerned: 'think',
  serious: 'neutral',
  calm: 'neutral',
  relaxed: 'neutral',
  cheerful: 'happy',
  joyful: 'happy',
  proud: 'happy',
  embarrassed: 'awkward',
  scared: 'surprise',
  afraid: 'surprise',
}

/**
 * 将 emotion 字符串标准化为系统支持的情感标签
 */
export function normalizeEmotion(emotion: string): string {
  return EMOTION_MAPPING[emotion.toLowerCase()] || emotion.toLowerCase()
}

/**
 * 生成情感标签字符串，如 <|EMOTE_HAPPY|>
 */
export function buildEmotionTag(emotion: string): string {
  const normalized = normalizeEmotion(emotion)
  return `<|EMOTE_${normalized.toUpperCase()}|>`
}

/**
 * 为通话模式下的消息添加前缀并标记为隐藏
 * @returns 是否应用了通话前缀
 */
export function applyCallModePrefix(
  message: ChatAssistantMessage | any,
  callType: 'voice' | 'video' | null,
): boolean {
  if (!callType)
    return false

  const callPrefix = callType === 'video' ? '视频通话' : '语音通话'
  const originalContent = typeof message.content === 'string' ? message.content : ''
  message.content = `[${callPrefix}]: ${originalContent}`
  message.isHidden = true
  return true
}

/**
 * 创建一条标准的 assistant 消息对象
 */
export function createAssistantMessage(
  text: string,
  extra?: Partial<ChatAssistantMessage>,
): ChatAssistantMessage {
  return {
    role: 'assistant',
    content: text,
    slices: [{ type: 'text' as const, text }],
    tool_results: [],
    timestamp: Date.now(),
    ...extra,
  }
}

/**
 * 格式化延迟时间为可读字符串
 */
export function formatDelayTime(seconds: number): string {
  if (seconds < 60)
    return `${seconds}秒`
  if (seconds < 3600)
    return `${Math.round(seconds / 60)}分钟`
  return `${Math.round(seconds / 3600)}小时`
}

const CHAT_IMAGE_MAX_DIMENSION = 512
const CHAT_IMAGE_COMPRESS_THRESHOLD = 100_000

/**
 * 统一的"图片已被省略"文本占位符，用于：
 * - compressLargeDataUrlsInMessages 里图片拉取失败
 * - chat.ts stripAllImagePartsFromMessages 降级重试剥离所有图片
 * - chat.ts purgeInvalidImagesFromStore 清理坏图后留空 content
 * 三处使用同一文案，保持 UI 语义一致，避免用户看到三种不同文本误以为是三种错误。
 */
export const IMAGE_PLACEHOLDER_TEXT = '[图片已省略]'

/**
 * 只拦截"结构上明显损坏"的 data URL：头部不是 data:image/、缺 base64 标志、payload 空、或尾部 base64 解不开。
 * 不做长度下限判断——用户导入的小尺寸合法 sticker（几百字节）也必须能通过。
 */
export function isValidImageDataUrl(value: unknown): value is string {
  if (typeof value !== 'string')
    return false
  if (!value.startsWith('data:image/'))
    return false
  const commaIdx = value.indexOf(',')
  if (commaIdx < 0)
    return false
  const header = value.slice(5, commaIdx)
  if (!header.includes(';base64'))
    return false
  const payload = value.slice(commaIdx + 1)
  if (payload.length === 0)
    return false
  // 真实解码尾部一小段，拦截"前缀看似合法但 base64 截断/乱码"的脏图
  try {
    const tail = payload.slice(-64).replace(/[^A-Z0-9+/=]/gi, '')
    if (tail.length === 0)
      return false

    atob(tail.length % 4 === 0 ? tail : tail.padEnd(tail.length + (4 - tail.length % 4), '='))
    return true
  }
  catch {
    return false
  }
}

export interface CanvasCompressOptions {
  /** 输出格式，默认 webp（空间最省，但老 provider 可能不识别） */
  format?: 'webp' | 'jpeg' | 'png'
  /** 压缩质量 0~1，默认 0.8（png 不生效） */
  quality?: number
}

function resolveMime(format: NonNullable<CanvasCompressOptions['format']>): string {
  if (format === 'jpeg')
    return 'image/jpeg'
  if (format === 'png')
    return 'image/png'
  return 'image/webp'
}

export function compressDataUrlViaCanvas(
  dataUrl: string,
  maxDim = CHAT_IMAGE_MAX_DIMENSION,
  options?: CanvasCompressOptions,
): Promise<string> {
  const format = options?.format ?? 'webp'
  const quality = options?.quality ?? 0.8
  return new Promise((resolve) => {
    try {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let { width, height } = img
        if (width > maxDim || height > maxDim) {
          const scale = maxDim / Math.max(width, height)
          width = Math.round(width * scale)
          height = Math.round(height * scale)
        }
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          resolve(dataUrl)
          return
        }
        ctx.drawImage(img, 0, 0, width, height)
        resolve(canvas.toDataURL(resolveMime(format), quality))
      }
      img.onerror = () => resolve(dataUrl)
      img.src = dataUrl
    }
    catch {
      resolve(dataUrl)
    }
  })
}

async function fetchImageAsDataUrl(url: string): Promise<string | null> {
  if (!isSafeStickerUrl(url))
    return null
  const res = await fetch(url, { signal: AbortSignal.timeout(8000) })
  if (!res.ok)
    return null
  const blob = await res.blob()
  if (!blob.type.startsWith('image/'))
    return null
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve((reader.result as string) || null)
    reader.onerror = () => resolve(null)
    reader.readAsDataURL(blob)
  })
}

const HTTP_IMAGE_CACHE_MAX = 100
const httpImageCache = new Map<string, string>()

export function shouldConvertImagePart(url: string): boolean {
  if (!url || typeof url !== 'string')
    return false
  if (url.startsWith('data:'))
    return false
  return isSafeStickerUrl(url)
}

export function convertHttpImageToDataUrl(url: string, maxDim = CHAT_IMAGE_MAX_DIMENSION): Promise<string | null> {
  const cacheKey = `${url}@${maxDim}`
  if (httpImageCache.has(cacheKey)) {
    // 真正 LRU：命中时把 key 移到尾部（Map 迭代顺序 = 插入顺序）
    const cached = httpImageCache.get(cacheKey)!
    httpImageCache.delete(cacheKey)
    httpImageCache.set(cacheKey, cached)
    return Promise.resolve(cached)
  }
  return new Promise((resolve) => {
    const resolveAndCache = (result: string | null) => {
      if (result && result !== url) {
        if (httpImageCache.size >= HTTP_IMAGE_CACHE_MAX)
          httpImageCache.delete(httpImageCache.keys().next().value!)
        httpImageCache.set(cacheKey, result)
      }
      resolve(result)
    }
    try {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let { width, height } = img
        if (width > maxDim || height > maxDim) {
          const scale = maxDim / Math.max(width, height)
          width = Math.round(width * scale)
          height = Math.round(height * scale)
        }
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          fetchImageAsDataUrl(url).then(resolveAndCache).catch(() => resolve(null))
          return
        }
        ctx.drawImage(img, 0, 0, width, height)
        try {
          resolveAndCache(canvas.toDataURL('image/webp', 0.8))
        }
        catch {
          fetchImageAsDataUrl(url).then(resolveAndCache).catch(() => resolve(null))
        }
      }
      img.onerror = () => fetchImageAsDataUrl(url).then(resolveAndCache).catch(() => resolve(null))
      img.src = url
    }
    catch {
      fetchImageAsDataUrl(url).then(resolveAndCache).catch(() => resolve(null))
    }
  })
}

export interface CompressImagesOptions {
  /** 每张图片转换失败时逐张回调（向后兼容） */
  onImageDropped?: (url: string) => void
  /**
   * 是否把 GIF 静态化为 WebP（只取第一帧）。默认 true，兼容不支持 image/gif 的 provider（Gemini 等）。
   * 只在不需要兼容时关闭（例如 UI 预览路径）。
   */
  gifToWebp?: boolean
}

export interface CompressImagesResult {
  /** 成功压缩/转换的 URL 映射，key=原始 URL（可能是 http(s) 或大 data URL），value=新 data URL */
  replacements: Map<string, string>
  /** 被从 content 数组中移除的 URL（http(s) 图片转换失败） */
  dropped: string[]
}

/**
 * 压缩消息中过大的 data URL 图片，并把 http(s) URL 预转换成 data URL。
 * ⚠️ 会直接修改传入的 messages 数组内容。
 *
 * 转换失败的 http(s) 图片会被从 content 中移除，并通过 onImageDropped 通知上层；
 * 若 content 数组被清空则补一个非空占位提示，避免下游 Gemini/Anthropic 等 provider 对 text:"" 报 400。
 */
export async function compressLargeDataUrlsInMessages(
  messages: Array<{ content?: unknown }>,
  options?: CompressImagesOptions,
): Promise<CompressImagesResult> {
  const replacements = new Map<string, string>()
  const dropped: string[] = []
  const gifToWebp = options?.gifToWebp ?? true
  for (const msg of messages) {
    if (!Array.isArray(msg.content))
      continue
    const content = msg.content as Array<{ type: string, image_url?: { url: string } }>
    const droppedIndices: number[] = []
    for (let i = 0; i < content.length; i++) {
      const part = content[i]
      if (part.type !== 'image_url' || !part.image_url?.url)
        continue
      const url: string = part.image_url.url
      // 🧹 预清洗：data URL 但明显损坏（头部异常 / base64 截断 / 非法字符）的图片直接静默丢弃，
      // 防止历史脏数据带到 provider 后报 convert_request_failed / failed to decode base64 data
      if (url.startsWith('data:') && !isValidImageDataUrl(url)) {
        droppedIndices.push(i)
        dropped.push(url)
        options?.onImageDropped?.(url)
        continue
      }
      if (shouldConvertImagePart(url)) {
        try {
          const result = await convertHttpImageToDataUrl(url)
          if (result == null) {
            droppedIndices.push(i)
            dropped.push(url)
            options?.onImageDropped?.(url)
          }
          else if (result !== url) {
            if (!isValidImageDataUrl(result)) {
              droppedIndices.push(i)
              dropped.push(url)
              options?.onImageDropped?.(url)
              continue
            }
            replacements.set(url, result)
            part.image_url.url = result
          }
        }
        catch {
          droppedIndices.push(i)
          dropped.push(url)
          options?.onImageDropped?.(url)
        }
        continue
      }
      if (!url.startsWith('data:'))
        continue
      if (gifToWebp && url.startsWith('data:image/gif')) {
        try {
          const result = await compressDataUrlViaCanvas(url)
          if (isValidImageDataUrl(result) && result !== url) {
            replacements.set(url, result)
            part.image_url.url = result
          }
        }
        catch { /* keep original */ }
        continue
      }
      if (url.length <= CHAT_IMAGE_COMPRESS_THRESHOLD)
        continue
      try {
        const result = await compressDataUrlViaCanvas(url)
        if (isValidImageDataUrl(result) && result.length < url.length) {
          replacements.set(url, result)
          part.image_url.url = result
        }
      }
      catch { /* keep original */ }
    }
    if (droppedIndices.length > 0) {
      for (let j = droppedIndices.length - 1; j >= 0; j--)
        content.splice(droppedIndices[j], 1)
      if (content.length === 0) {
        // 全部 image part 失败时补一个对用户有语义的占位,
        // 避免 Gemini/Anthropic 等 provider 对 text:"" 报 content must be non-empty
        content.push({ type: 'text', text: IMAGE_PLACEHOLDER_TEXT } as unknown as { type: string, image_url?: { url: string } })
      }
    }
  }
  return { replacements, dropped }
}

/**
 * 判断错误是否是 provider 抛出的 base64 图片解码失败
 */
export function isImageDecodeError(error: unknown): boolean {
  const msg = (error as Error)?.message || String(error)
  return msg.includes('convert_request_failed') || msg.includes('failed to decode base64 data')
}

/**
 * 返回一个新数组：把每条消息 content 数组里的所有 image_url part 剔除。
 * 用于 convert_request_failed 发生后的降级重试（不改动原始数组引用的 store 数据）。
 */
export function stripAllImagePartsFromMessages<T extends { content?: unknown }>(msgs: T[]): T[] {
  return msgs.map((msg) => {
    if (!Array.isArray(msg.content))
      return msg
    const filtered = (msg.content as Array<{ type?: string }>).filter(part => part?.type !== 'image_url')
    if (filtered.length === 0) {
      filtered.push({ type: 'text', text: IMAGE_PLACEHOLDER_TEXT } as unknown as { type?: string })
    }
    return { ...msg, content: filtered as unknown as T['content'] }
  })
}

/**
 * 解析 dataURL 头，提取 mediaType（如 image/jpeg）和 base64 payload。
 * 损坏 / 非 base64 的 dataURL 返回 null。
 */
export function parseDataUrl(url: string): { mediaType: string, data: string } | null {
  if (typeof url !== 'string' || !url.startsWith('data:'))
    return null
  const commaIdx = url.indexOf(',')
  if (commaIdx < 0)
    return null
  const header = url.slice(5, commaIdx)
  if (!header.includes(';base64'))
    return null
  const mediaType = header.split(';')[0]
  const data = url.slice(commaIdx + 1)
  if (!mediaType || !data)
    return null
  return { mediaType, data }
}

/**
 * Anthropic 原生 image block 与 OpenAI image_url part 的兜底占位。
 * 出错时以 text part 形式返回，避免 Anthropic 对 unknown content type 直接 400。
 */
function makeImageFallbackTextPart(): Record<string, unknown> {
  return { type: 'text', text: IMAGE_PLACEHOLDER_TEXT }
}

/**
 * 把 OpenAI 风格的 `{ type: 'image_url', image_url: { url } }` part 转成 Anthropic 原生
 * `{ type: 'image', source: { type: 'base64', media_type, data } }`。
 * - 非 image_url part 原样返回（保留 cache_control 等额外字段）
 * - http(s) URL 先转成 dataURL（命中现有 LRU），失败 → text 占位
 * - dataURL 损坏 → text 占位
 */
export async function convertImageUrlPartToAnthropic(
  part: unknown,
): Promise<Record<string, unknown>> {
  if (typeof part !== 'object' || part === null)
    return makeImageFallbackTextPart()
  const p = part as Record<string, unknown>
  if (p.type !== 'image_url') {
    return p
  }
  const imageUrl = p.image_url as { url?: string } | undefined
  const rawUrl = imageUrl?.url
  if (typeof rawUrl !== 'string' || rawUrl.length === 0)
    return makeImageFallbackTextPart()

  let url = rawUrl
  if (!url.startsWith('data:')) {
    const converted = await convertHttpImageToDataUrl(url)
    if (!converted)
      return makeImageFallbackTextPart()
    url = converted
  }

  if (!isValidImageDataUrl(url))
    return makeImageFallbackTextPart()
  const parsed = parseDataUrl(url)
  if (!parsed)
    return makeImageFallbackTextPart()

  const block: Record<string, unknown> = {
    type: 'image',
    source: { type: 'base64', media_type: parsed.mediaType, data: parsed.data },
  }
  if ((p as { cache_control?: unknown }).cache_control)
    block.cache_control = (p as { cache_control?: unknown }).cache_control
  return block
}

/**
 * 把整条 message.content（字符串 or part 数组）转成 Anthropic 兼容形态。
 * 字符串原样返回；数组 map 调用 convertImageUrlPartToAnthropic；
 * 全部失败导致数组为空时补一个 text 占位，避免 Anthropic 对 content must be non-empty 报错。
 */
export async function convertContentArrayToAnthropic(
  content: unknown,
): Promise<unknown> {
  if (typeof content === 'string' || !Array.isArray(content))
    return content
  const converted: Record<string, unknown>[] = []
  for (const part of content)
    converted.push(await convertImageUrlPartToAnthropic(part))
  if (converted.length === 0)
    converted.push(makeImageFallbackTextPart())
  return converted
}

/** 含 quote 字段的消息（跨通道公用，不依赖具体 Message 子类型）。 */
interface RawMsgWithQuote {
  role: unknown
  quote?: { senderName: string, content: string }
}

/**
 * 将消息的 quote 字段嵌入 content，生成引用前缀。
 * 各 LLM 通道（Anthropic / Gemini / Responses / compat）共用，避免 4 处 copy-paste。
 *
 * @param rawMsg  已去响应式的消息对象（含可选 quote 字段）
 * @param content 原始 content（string 或 part 数组）
 * @returns 嵌入引用前缀后的 content；无 quote 或非 user 消息原样返回
 */
export function embedQuotePrefix(rawMsg: object, content: unknown): unknown {
  const msg = rawMsg as RawMsgWithQuote
  if (msg.role !== 'user' || !msg.quote)
    return content
  const q = msg.quote
  const rawContent = q.content || ''
  // Unicode 感知截断：用 spread 展开以正确处理 emoji 等多字节字符
  const quotedContent = rawContent.length > 50 ? `${[...rawContent].slice(0, 50).join('')}...` : rawContent
  const prefix = `[引用 ${q.senderName} 的消息："${quotedContent}"] `
  if (typeof content === 'string')
    return prefix + content
  if (Array.isArray(content))
    return [{ type: 'text', text: prefix }, ...content]
  return content
}
